/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import * as AST from './AST';
import { parser as SAXParser, Tag as SAXTag } from 'sax';

const html2ast: Record<string, AST.NODE_TYPE> = {
    root: AST.NODE_TYPE.ROOT,
    a: AST.NODE_TYPE.LINK,
    b: AST.NODE_TYPE.STRONG,
    br: AST.NODE_TYPE.BREAK,
    div: AST.NODE_TYPE.BLOCK,
    em: AST.NODE_TYPE.EMPHASIS,
    h1: AST.NODE_TYPE.HEADING,
    h2: AST.NODE_TYPE.HEADING,
    h3: AST.NODE_TYPE.HEADING,
    h4: AST.NODE_TYPE.HEADING,
    h5: AST.NODE_TYPE.HEADING,
    h6: AST.NODE_TYPE.HEADING,
    hr: AST.NODE_TYPE.THEMATIC_BREAK,
    i: AST.NODE_TYPE.EMPHASIS,
    img: AST.NODE_TYPE.IMAGE,
    li: AST.NODE_TYPE.LIST_ITEM,
    ol: AST.NODE_TYPE.LIST,
    p: AST.NODE_TYPE.PARAGRAPH,
    span: AST.NODE_TYPE.SPAN,
    strong: AST.NODE_TYPE.STRONG,
    ul: AST.NODE_TYPE.LIST,
};

const ast2html: Record<AST.NODE_TYPE, string> = {
    [AST.NODE_TYPE.BLOCK]: 'div',
    [AST.NODE_TYPE.BREAK]: 'br',
    [AST.NODE_TYPE.EMPHASIS]: 'em',
    [AST.NODE_TYPE.HEADING]: 'h1',
    [AST.NODE_TYPE.IMAGE]: 'img',
    [AST.NODE_TYPE.LINK]: 'a',
    [AST.NODE_TYPE.LIST]: 'ul',
    [AST.NODE_TYPE.LIST_ITEM]: 'li',
    [AST.NODE_TYPE.PARAGRAPH]: 'p',
    [AST.NODE_TYPE.ROOT]: 'div',
    [AST.NODE_TYPE.SPAN]: 'span',
    [AST.NODE_TYPE.STRONG]: 'strong',
    [AST.NODE_TYPE.TEXT]: 'span',
    [AST.NODE_TYPE.THEMATIC_BREAK]: 'hr',
};

export function tagNameToNode(tagName: string): AST.NODE_TYPE | undefined {
    return html2ast[tagName];
}

export function nodeTypeToTag(node: AST.NODE_TYPE): string {
    return ast2html[node] || 'div';
}

const attrMap: Record<string, string> = {
    alt: 'alt',
    class: 'className',
    href: 'url',
    id: 'id',
    src: 'url',
    start: 'start',
    target: 'target',
    title: 'title',
};

function makeAstNode(tag: SAXTag) {
    const type = tagNameToNode(tag.name);
    if (!type || tag.name === 'root') {
        return;
    }
    const result: Record<string, string | number | boolean> = {};
    for (const attr in tag.attributes) {
        if (tag.attributes.hasOwnProperty(attr) && attr in attrMap) {
            result[attrMap[attr]] = tag.attributes[attr];
        }
    }
    if (type === AST.NODE_TYPE.HEADING) {
        result.depth = parseInt(tag.name[1], 10);
    }
    if (tag.name === 'ol') {
        result.ordered = true;
    }
    return AST.create(type, result as any);
}

const voidElements = [
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
];

function isVoid(tag: SAXTag) {
    return voidElements.indexOf(tag.name) >= 0;
}

export function parseHtml(src: string): AST.Root {
    src = `<root>${src}</root>`;
    const root: AST.Root = {
        type: AST.NODE_TYPE.ROOT,
        children: [],
    };
    const tagStack: AST.Parent[] = [root];
    let currentNode: AST.Parent = root;

    const parser = SAXParser(false, {
        lowercase: true,
        noscript: true,
        normalize: true,
    });

    // Ignore parser errors
    parser.onerror = console.error; // () => void 0;

    parser.onopentag = (node: SAXTag) => {
        if (tagNameToNode(node.name)) {
            const newNode = makeAstNode(node);
            if (newNode) {
                currentNode.children.push(newNode as AST.Content);

                if (!node.isSelfClosing && !isVoid(node)) {
                    tagStack.push(currentNode as AST.Parent);
                    currentNode = newNode as AST.Parent;
                }
            }
        }
    };

    parser.onclosetag = (node) => {
        /* Do not filter out void tags
         * it's better to strip and ignore closing tags
         */
        if (currentNode.type === tagNameToNode(node)) {
            // eslint-disable-next-line
            currentNode = tagStack.pop()!;
        }
    };
    parser.ontext = (value) => {
        currentNode.children.push(new AST.Text({ value }));
    };

    parser.write(src);
    parser.close();

    return root;
}

export default parseHtml;
